import {useEffect,useMemo,useState} from 'react';
import {Activity,Clock3,FileText,RefreshCw,ShieldCheck} from 'lucide-react';
import {getPrimaryMembership} from '../lib/agentordinaryWorkspace';
import {supabase} from '../lib/supabase';
import {workActivityText} from '../lib/agentMessagePresentation';

type AgentEvent={id:string;source:'Work'|'Event';title:string;detail:string;type:string;time:string};
async function getAgentHistory(workspaceId:string,agentId:string):Promise<AgentEvent[]>{
 const membership=await getPrimaryMembership();
 if(membership.workspace_id!==workspaceId)throw new Error('Workspace access does not match this employee.');
 const [activity,events,taskUpdates]=await Promise.all([
  supabase.from('agentopia_responsibility_activity')
   .select('id,title,detail,event_type,created_at')
   .eq('workspace_id',workspaceId).eq('agent_id',agentId)
   .order('created_at',{ascending:false}).limit(75),
  supabase.from('agentopia_events')
   .select('id,event_type,occurred_at,payload')
   .eq('workspace_id',workspaceId).eq('agent_id',agentId)
   .order('occurred_at',{ascending:false}).limit(75),
  supabase.from('agentopia_messages')
   .select('id,content,created_at')
   .eq('workspace_id',workspaceId).eq('sender_agent_id',agentId)
   .eq('source_system','agentopia-task-state')
   .order('created_at',{ascending:false}).limit(100)
 ]);
 if(activity.error)throw activity.error;
 if(events.error)throw events.error;
 if(taskUpdates.error)throw taskUpdates.error;
 const updateEvents:AgentEvent[]=(taskUpdates.data||[]).flatMap(x=>{
  const status=workActivityText(x.content);
  if(!status)return [];
  return [{id:'task-'+x.id,source:'Work' as const,title:status.title,
   detail:status.detail,type:status.status,time:String(x.created_at||'')}];
 });
 const rows:AgentEvent[]=[
  ...updateEvents,
  ...(activity.data||[]).map(x=>({id:'work-'+x.id,source:'Work' as const,title:String(x.title||x.event_type||'Work activity'),
    detail:String(x.detail||''),type:String(x.event_type||'unknown'),time:String(x.created_at||'')})),
  ...(events.data||[]).map(x=>{
   const data=x.payload&&typeof x.payload==='object'&&!Array.isArray(x.payload)?x.payload as Record<string,unknown>:{};
   const eventType=String(x.event_type||'unknown');
   return {id:'event-'+x.id,source:'Event' as const,
    title:typeof data.title==='string'?data.title:eventType.replace(/[._]/g,' '),
    detail:typeof data.summary==='string'?data.summary:typeof data.detail==='string'?data.detail:'',
    type:eventType,time:String(x.occurred_at||'')};
  })
 ];
 return rows.filter(x=>x.time).sort((a,b)=>b.time.localeCompare(a.time)).slice(0,90);
}
export default function AgentRecordedActivity({workspaceId,agentId,previewOnly=false}:{workspaceId?:string;agentId:string;previewOnly?:boolean}){
 const [events,setEvents]=useState<AgentEvent[]>([]);
 const [loading,setLoading]=useState(!previewOnly);
 const [error,setError]=useState('');
 const [filter,setFilter]=useState<'All'|'Work'|'Event'>('All');
 const [revision,setRevision]=useState(0);
 useEffect(()=>{
  if(previewOnly||!workspaceId){setEvents([]);setLoading(false);return;}
  let active=true;setLoading(true);setError('');
  getAgentHistory(workspaceId,agentId).then(rows=>{if(active)setEvents(rows)}).catch(e=>{if(active)setError(e instanceof Error?e.message:'Could not load recorded activity')})
   .finally(()=>{if(active)setLoading(false)});
  return()=>{active=false};
 },[workspaceId,agentId,previewOnly,revision]);
 const visible=useMemo(()=>events.filter(e=>filter==='All'||e.source===filter),[events,filter]);
 return <section className="ab4-recorded-activity" aria-label="Employee recorded activity">
  <header className="ab4-recorded-top"><div><Activity size={19}/><div><strong>Recorded activity</strong><small>Actual event history, timestamps and workflow details. Records are not proof of delivery.</small></div></div>
   <button type="button" disabled={loading||previewOnly} onClick={()=>setRevision(x=>x+1)}><RefreshCw size={15}/> Refresh</button>
  </header>
  <div className="ab4-activity-filters" role="group" aria-label="Filter recorded activity">
   {(['All','Work','Event'] as const).map(k=><button type="button" key={k} aria-pressed={filter===k} className={filter===k?'active':''} onClick={()=>setFilter(k)}>{k}{k==='All'?' ('+events.length+')':''}</button>)}
  </div>
  {loading&&<p role="status">Loading employee activity…</p>}
  {error&&<p role="alert">{error}</p>}
  {!loading&&!error&&previewOnly&&<p>Preview has no live activity. Sign in to see employee event records.</p>}
  {!loading&&!error&&!previewOnly&&visible.length===0&&<p>No recorded events for this employee in the selected category.</p>}
  <div className="ab4-recorded-list">
   {visible.map(event=><article key={event.id} className="ab4-recorded-item"><span className="ab4-recorded-symbol"><FileText size={17}/></span>
    <div><strong>{event.title}</strong><small>{event.source} · {event.type}</small>{event.detail&&<p>{event.detail}</p>}</div>
    <time dateTime={event.time}><Clock3 size={12}/>{new Date(event.time).toLocaleString([],{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'})}</time>
   </article>)}
  </div>
  <small className="ab4-recorded-note"><ShieldCheck size={14}/> Read-only activity. No execution, pause, or resume commands are issued here.</small>
 </section>;
}
